import { queryOptions, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { BoardPayload, Card, Column, Group, Label } from '@rm/shared';
import { boardApi } from '../../api/endpoints.js';

export const boardKey = (boardId: string) => ['board', boardId] as const;

/**
 * The whole board in one cache entry.
 *
 * Splitting cards into `['column', id]` entries would mean constantly
 * reconciling them against each other during a drag; a single normalised entry
 * makes optimistic reordering a pure function over one object.
 */
export function boardQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: boardKey(boardId),
    queryFn: () => boardApi.get(boardId),
    staleTime: 60_000,
  });
}

export function useBoard(boardId: string) {
  return useSuspenseQuery(boardQueryOptions(boardId));
}

/** Applies a local change to the cached board without a refetch. */
export function useBoardPatch(boardId: string) {
  const queryClient = useQueryClient();

  return (patch: (board: BoardPayload) => BoardPayload) => {
    queryClient.setQueryData<BoardPayload>(boardKey(boardId), (board) =>
      board ? patch(board) : board,
    );
  };
}

/* ------------------------------------------------------------------ *
 * Pure helpers used by both optimistic updates and realtime patches
 * ------------------------------------------------------------------ */

export function replaceCard(board: BoardPayload, card: Card): BoardPayload {
  const exists = board.cards.some((c) => c.id === card.id);
  return {
    ...board,
    cards: exists
      ? board.cards.map((c) => (c.id === card.id ? card : c))
      : [...board.cards, card],
  };
}

export function removeCard(board: BoardPayload, cardId: string): BoardPayload {
  return { ...board, cards: board.cards.filter((c) => c.id !== cardId) };
}

export function moveCardLocally(
  board: BoardPayload,
  cardId: string,
  toColumnId: string,
  rank: string,
): BoardPayload {
  return {
    ...board,
    cards: board.cards.map((c) =>
      c.id === cardId ? { ...c, columnId: toColumnId, rank } : c,
    ),
  };
}

export function replaceColumn(board: BoardPayload, column: Column): BoardPayload {
  const exists = board.columns.some((c) => c.id === column.id);
  return {
    ...board,
    columns: exists
      ? board.columns.map((c) => (c.id === column.id ? column : c))
      : [...board.columns, column],
  };
}

export function removeColumn(board: BoardPayload, columnId: string): BoardPayload {
  return {
    ...board,
    columns: board.columns.filter((c) => c.id !== columnId),
    // Cards on a deleted column are gone server-side too (the FK cascades).
    cards: board.cards.filter((c) => c.columnId !== columnId),
  };
}

export function replaceLabel(board: BoardPayload, label: Label): BoardPayload {
  const exists = board.labels.some((l) => l.id === label.id);
  return {
    ...board,
    labels: exists ? board.labels.map((l) => (l.id === label.id ? label : l)) : [...board.labels, label],
  };
}

export function removeLabel(board: BoardPayload, labelId: string): BoardPayload {
  return {
    ...board,
    labels: board.labels.filter((l) => l.id !== labelId),
    cards: board.cards.map((c) =>
      c.labelIds.includes(labelId)
        ? { ...c, labelIds: c.labelIds.filter((id) => id !== labelId) }
        : c,
    ),
  };
}

export function replaceGroup(board: BoardPayload, group: Group): BoardPayload {
  const exists = board.groups.some((g) => g.id === group.id);
  return {
    ...board,
    groups: exists ? board.groups.map((g) => (g.id === group.id ? group : g)) : [...board.groups, group],
  };
}

export function removeGroup(board: BoardPayload, groupId: string): BoardPayload {
  return {
    ...board,
    groups: board.groups.filter((g) => g.id !== groupId),
    cards: board.cards.map((c) => (c.groupId === groupId ? { ...c, groupId: null } : c)),
  };
}

/** Cards in one column, in rank order. */
export function cardsInColumn(board: BoardPayload, columnId: string): Card[] {
  return board.cards
    .filter((c) => c.columnId === columnId)
    .sort((a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0));
}

/**
 * Every column's cards, rank-ordered, in one pass over the card list.
 *
 * The render path used to call `cardsInColumn` once per column, which rescans
 * and re-sorts the whole board for each — fine for a demo board, quadratic as
 * the board fills up. Ranks sort as plain strings by design (the server stores
 * them `COLLATE "C"` for exactly this reason), so a lexicographic compare here
 * matches the server's order precisely.
 */
export function groupCardsByColumn(cards: Card[]): Map<string, Card[]> {
  const byColumn = new Map<string, Card[]>();

  for (const card of cards) {
    const bucket = byColumn.get(card.columnId);
    if (bucket) bucket.push(card);
    else byColumn.set(card.columnId, [card]);
  }

  for (const bucket of byColumn.values()) {
    bucket.sort((a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0));
  }

  return byColumn;
}

export function sortedColumns(board: BoardPayload): Column[] {
  return [...board.columns].sort((a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0));
}
